import { NETWORK_SUMMARY, NETWORK_FOUNDING, NETWORK_SEPARATE, NETWORK_TAGLINE } from "@/lib/network";
import { createFileRoute } from "@tanstack/react-router";
import { Section, SectionHeading, CTASection } from "@/components/layout";

const TITLE = "About Tables and Bridges | Law Enforcement Community Engagement";
const DESC =
  "Tables and Bridges is a national movement and methodology platform for law enforcement Community Engagement, founded by practitioner Carlai Moore.";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: "https://tablesandbridges.com/about" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://tablesandbridges.com/about" }],
  }),
  component: AboutPage,
});

const ECOSYSTEM = [
  {
    n: "01",
    title: "The Trust Operating System",
    desc: "The professional methodology. It gives practitioners and agencies a disciplined way to locate trust, diagnose what is weakening it, choose the right level of intervention, and prove what changed.",
    href: "/trust-operating-system",
  },
  {
    n: "02",
    title: "CEO Practitioner Network",
    desc: `${NETWORK_TAGLINE} ${NETWORK_SUMMARY} ${NETWORK_FOUNDING} ${NETWORK_SEPARATE}`,
    href: "https://ceopractitionernetwork.com",
    external: true,
  },
  {
    n: "03",
    title: "Tables and Bridges, the Podcast",
    desc: "The public conversation. Honest field conversations with practitioners, community partners, and leaders about what building trust actually requires.",
    href: "/podcast",
  },
];

function AboutPage() {
  return (
    <div>
      {/* HERO / MISSION */}
      <section className="relative bg-navy text-warm-white overflow-hidden">
        <div aria-hidden className="absolute inset-0 opacity-30">
          <svg className="w-full h-full" viewBox="0 0 1440 700" preserveAspectRatio="xMidYMid slice" fill="none">
            <defs>
              <pattern id="about-grid" width="80" height="80" patternUnits="userSpaceOnUse">
                <path d="M80 0H0V80" stroke="#F7F5EF" strokeOpacity="0.3" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="1440" height="700" fill="url(#about-grid)" />
            <path d="M-40 560 L520 380 L920 440 L1500 240" stroke="#F2B84B" strokeWidth="1.25" />
          </svg>
        </div>
        <div aria-hidden className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(36,77,58,0.35),transparent_55%)]" />
        <div className="container-editorial relative z-10 pt-28 pb-24 md:pt-36 md:pb-32">
          <p className="eyebrow mb-8">Our Mission</p>
          <h1 className="max-w-5xl font-display text-[clamp(2.25rem,5vw,4.5rem)] leading-[1.02] tracking-[-0.02em]">
            Equip law enforcement Community Engagement professionals and agencies
            to build trust{" "}
            <span className="italic text-gold">from the inside out.</span>
          </h1>
          <div className="mt-10 max-w-3xl space-y-5 text-warm-white/85 text-lg leading-relaxed">
            <p>
              Tables and Bridges exists to give Community Engagement the tools,
              training, methodology, and professional support the work requires,
              so practitioners can strengthen teams, transform relationships, and
              create measurable impact in the communities they serve.
            </p>
            <p>
              We are a movement, not a consultancy. The people responsible for
              building trust between law enforcement agencies and their
              communities carry significant responsibility, often without a
              shared methodology, an established professional standard, or a
              reliable place to learn from others doing the same work. Tables and
              Bridges was built to change that.
            </p>
          </div>
          <div className="mt-12 flex flex-wrap gap-4">
            <a href="https://ceopractitionernetwork.com" target="_blank" rel="noopener noreferrer" className="btn-base btn-primary">Explore the CEO Practitioner Network</a>
            <a href="/for-agencies" className="btn-base btn-secondary-dark">
              Work With Tables and Bridges
            </a>
          </div>
        </div>
      </section>

      {/* VISION */}
      <Section tone="warm-white">
        <SectionHeading
          eyebrow="Our Vision"
          title="A stronger future for the profession and the communities it serves."
        />
        <div className="mt-10 max-w-4xl space-y-5 text-navy/85 text-lg leading-relaxed">
          <p>
            We are working toward a future where every law enforcement Community
            Engagement professional and leader has the courage, skill, and
            organizational support required to rebuild trust, bridge divides, and
            cultivate communities that thrive together.
          </p>
          <p>
            That future is not built by one department, one officer, or one good
            year. It is built by a profession that shares language, standards, and
            evidence, and by communities and government partners who are invited
            to the table rather than informed after the fact.
          </p>
        </div>
      </Section>

      {/* FOUNDER */}
      <Section tone="deep-green">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.3fr] items-start">
          <div>
            <p className="eyebrow mb-5">The Founder</p>
            <h2 className="font-display">Built because the gap was real.</h2>
            <span aria-hidden className="divider-gold mt-6" />
          </div>
          <div className="space-y-5 text-warm-white/90 leading-relaxed text-lg measure-body">
            <p>
              Carlai Moore is a Police Sergeant and Community Engagement leader.
              She did not build Tables and Bridges as a consultant looking at the
              profession from the outside. She built it as someone who lived the
              work, in a real agency, in a real community, with real consequences
              when trust broke.
            </p>
            <p>
              She was asked to build partnerships, create programs, respond when
              relationships were strained, advise command staff, represent her
              agency, support community members, and demonstrate impact. What she
              was not given was a methodology, a professional standard, or peers
              who understood the role. That gap was not a personal shortcoming. It
              was a structural one, shared by practitioners across the country.
            </p>
            <p>
              Tables and Bridges is her answer to it: clearer language, stronger
              methodology, honest professional development, and a community that
              understands the work.
            </p>
            <div className="flex flex-wrap gap-4 pt-4">
              <a href="/book-carlai" className="btn-base btn-primary">Book Carlai</a>
            </div>
          </div>
        </div>
      </Section>

      {/* WHAT WE BELIEVE */}
      <Section tone="warm-white">
        <SectionHeading
          eyebrow="What we believe"
          title="Community Engagement is a professional discipline."
        />
        <div className="mt-8 max-w-4xl space-y-6 text-navy/85 text-lg leading-relaxed">
          <p>
            Community Engagement is not simply event planning, public visibility,
            social media, or maintaining positive relationships. The work requires
            professionals who can diagnose trust conditions, design meaningful
            engagement, understand power and authority, facilitate difficult
            conversations, advise command staff, document outcomes, and help
            agencies move from activity to lasting change.
          </p>
          <p>Tables and Bridges gives that work a professional structure.</p>
        </div>
        <div className="mt-14 border-l-2 border-gold pl-8 py-2 max-w-3xl">
          <p className="font-display text-3xl md:text-4xl leading-tight text-navy">
            The role has not been too small.
          </p>
          <p className="font-display text-3xl md:text-4xl leading-tight text-navy/70 italic">
            It has been described too narrowly.
          </p>
        </div>
      </Section>

      {/* ECOSYSTEM */}
      <Section tone="ink">
        <SectionHeading
          eyebrow="The Ecosystem"
          title="Tables and Bridges is the parent organization."
          lede="One movement powering three connected parts. Agency training, assessment, consulting, facilitation, and implementation support are delivered directly by Tables and Bridges and draw on all three."
        />
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {ECOSYSTEM.map((c) => (
            <a
              key={c.title}
              href={c.href}
              target={c.external ? "_blank" : undefined}
              rel={c.external ? "noopener noreferrer" : undefined}
              className="group flex flex-col border border-warm-white/15 p-8 hover:border-gold transition-colors"
            >
              <span className="font-display text-gold text-3xl leading-none">{c.n}</span>
              <h3 className="mt-6 font-display text-warm-white text-xl">{c.title}</h3>
              <p className="mt-4 text-sm text-warm-white/75 leading-relaxed">{c.desc}</p>
              <span className="mt-8 inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-gold">
                Explore <span aria-hidden>→</span>
              </span>
            </a>
          ))}
        </div>
      </Section>

      {/* WHO WE SERVE */}
      <Section tone="warm-white">
        <SectionHeading
          eyebrow="Who we serve"
          title="Built around the practitioner."
        />
        <div className="mt-10 max-w-4xl space-y-5 text-navy/85 text-lg leading-relaxed">
          <p>
            Tables and Bridges serves Community Engagement officers, supervisors,
            and unit leaders, along with community relations, crime prevention,
            neighborhood engagement, and youth engagement officers. It also serves
            command staff responsible for Community Engagement strategy and agency
            leaders working to strengthen internal and external trust.
          </p>
          <p>
            Our primary responsibility is to the practitioner doing the work. When
            Community Engagement professionals are better equipped, agencies
            receive stronger insight, programs become more intentional,
            partnerships become more credible, and communities experience more
            consistent follow-through.
          </p>
        </div>
      </Section>

      <CTASection
        eyebrow="Next"
        title="The profession is ready for a stronger foundation."
        primary={{ label: "Explore the CEO Practitioner Network", href: "https://ceopractitionernetwork.com", external: true }}
        secondary={{ label: "Work With Tables and Bridges", href: "/for-agencies" }}
        tone="navy"
      />
    </div>
  );
}
